<script lang="ts" setup>
import type { PickUpPointType } from '~/api/common';
import OrderAPI from '~/api/order';

const BLOCK_KEY = 'how-to-obtain-saving';
const TEST_ID = 'order-how-to-obtain';

enum ObtainType {
  // TODO: нужно ли еще
  // express,
  email = 'email',
  courier = 'courier',
  points = 'pvz',
  pickup = 'pickup',
  pickup_friend = 'pickup_friend',
}

defineProps<{
  bordered?: boolean;
}>();

const { t } = useI18n();
const orderStore = useOrderStore();
const modalStore = useModalStore();
const userStore = useUserStore();

const selectedObtainType = ref<ObtainType>();
const selectedAddressId = ref<number | null>();
const isVisibleAddressForm = ref(false);
const selectedPickUpPoint = ref<PickUpPointType>();

const isEnabled = computed(() => !!orderStore.delivery.variants.length);
const isEmailEnabled = computed(() =>
  orderStore.delivery.variants.some((el) => el.type === ObtainType.email),
);
const isCourierEnabled = computed(() =>
  orderStore.delivery.variants.some((el) => el.type === ObtainType.courier),
);
const isPointsEnabled = computed(() =>
  orderStore.delivery.variants.some((el) => el.type === ObtainType.points),
);
const isPickupEnabled = computed(() =>
  orderStore.delivery.variants.some((el) => el.type === ObtainType.pickup),
);
const isPickupFriendEnabled = computed(() =>
  orderStore.delivery.variants.some(
    (el) => el.type === ObtainType.pickup_friend,
  ),
);

const onAddressCreated = (value: { addressId?: number }) => {
  isVisibleAddressForm.value = false;
  orderStore.delivery.addresses = userStore.profile.addresses;

  if (value.addressId) {
    nextTick(() => {
      selectedObtainType.value = ObtainType.courier;
      selectedAddressId.value = value.addressId;
    });
  }
};
const onSelectPoint = (point: PickUpPointType) => {
  selectedPickUpPoint.value = point;
};
const onChangeObtainType = (type: ObtainType) => {
  selectedObtainType.value = type;
  selectedAddressId.value = null;

  orderStore.setBlockKey(BLOCK_KEY);
  if (
    [ObtainType.pickup, ObtainType.pickup_friend, ObtainType.points].includes(
      type,
    )
  ) {
    OrderAPI.selectDelivery(
      {
        body: {
          type,
          pvzId:
            type === ObtainType.points
              ? selectedPickUpPoint.value?.id
              : undefined,
        },
      },
      {
        onError: () => {
          modalStore.showError({
            title: t('message.error'),
            action: t('message.action'),
          });
        },
      },
    )?.then((data) => {
      orderStore.updateOrderState(data);
      orderStore.deleteBlockKey(BLOCK_KEY);
    });
  }
};

watch(
  () => orderStore.delivery.current,
  (v) => {
    if (v) {
      selectedObtainType.value = v.type as ObtainType;
      if (v.type === ObtainType.courier) {
        selectedAddressId.value = v.addressId;
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="orderHowToObtain"
    :class="{ orderHowToObtain_bordered: bordered }"
  >
    <div
      class="orderHowToObtain__header"
      :class="{ orderHowToObtain__header_enabled: isEnabled }"
    >
      {{ t('title') }}
    </div>

    <BaseCollapse
      :visible="isEnabled"
      :fixed-duration="300"
      content-class="orderHowToObtain__content"
    >
      <BaseRadioPlate
        v-if="isEmailEnabled"
        :checked="selectedObtainType === ObtainType.email"
        :value="ObtainType.email"
        name="orderHowToObtain"
        :data-test-id="`${TEST_ID}-${ObtainType.email}`"
      >
        {{ t('variant.email.label') }}
      </BaseRadioPlate>

      <!-- <BaseRadioPlate @change="onChangeObtainType(ObtainType.express)" :checked="selectedObtainType === ObtainType.express">-->
      <!--   Экспресс-доставка за 2 часа-->
      <!-- </BaseRadioPlate>-->

      <div v-if="isCourierEnabled">
        <BaseRadioPlate
          @update:checked="onChangeObtainType(ObtainType.courier)"
          :checked="selectedObtainType === ObtainType.courier"
          :value="ObtainType.courier"
          name="orderHowToObtain"
          :data-test-id="`${TEST_ID}-${ObtainType.courier}`"
        >
          {{ t('variant.courier.label') }}
        </BaseRadioPlate>
        <BaseCollapse
          :visible="selectedObtainType === ObtainType.courier"
          content-class="orderHowToObtain__courierGroup"
        >
          <OrderAddressWithTimeItem
            v-for="address in orderStore.delivery.addresses"
            :key="address.id"
            @select="selectedAddressId = address.id"
            :selected="selectedAddressId === address.id"
            :address="address"
          />

          <button
            @click="isVisibleAddressForm = !isVisibleAddressForm"
            class="orderHowToObtain__courierAddAddress"
            :data-test-id="`${TEST_ID}-${ObtainType.courier}-append-address`"
          >
            {{ t('variant.courier.address') }}
            <BaseExpandIcon
              :expanded="isVisibleAddressForm"
              class="orderHowToObtain__courierAddAddress_icon"
            />
          </button>
          <BaseCollapse
            :visible="isVisibleAddressForm"
            destructible
            content-class="orderHowToObtain__courierAddressForm"
          >
            <ProfileAddressForm @completed="onAddressCreated" />
          </BaseCollapse>
        </BaseCollapse>
      </div>

      <div v-if="isPointsEnabled">
        <BaseRadioPlate
          @update:checked="onChangeObtainType(ObtainType.points)"
          :checked="selectedObtainType === ObtainType.points"
          :value="ObtainType.points"
          name="orderHowToObtain"
          :data-test-id="`${TEST_ID}-${ObtainType.points}`"
        >
          {{ t('variant.points.label') }}
        </BaseRadioPlate>
        <BaseCollapse
          :visible="selectedObtainType === ObtainType.points"
          content-class="orderHowToObtain__pointsGroup"
        >
          <OrderPickupPointsForm @select="onSelectPoint" />
        </BaseCollapse>
      </div>

      <div v-if="isPickupEnabled">
        <BaseRadioPlate
          @update:checked="onChangeObtainType(ObtainType.pickup)"
          :checked="selectedObtainType === ObtainType.pickup"
          :value="ObtainType.pickup"
          name="orderHowToObtain"
          :data-test-id="`${TEST_ID}-${ObtainType.pickup}`"
        >
          {{ t('variant.pickup.label') }}
        </BaseRadioPlate>
        <BaseCollapse
          :visible="selectedObtainType === ObtainType.pickup"
          content-class="orderHowToObtain__pickup"
        >
          <i18n-t keypath="variant.pickup.description">
            <template #address>{{ t('company.address.full') }}</template>
            <template #openingHours>{{
              t('company.shop_opening_hours')
            }}</template>
            <template #additional>
              <span class="orderHowToObtain__pickup_additional">
                {{ t('variant.pickup.additional') }}
              </span>
            </template>
          </i18n-t>
        </BaseCollapse>
      </div>

      <div v-if="isPickupFriendEnabled">
        <BaseRadioPlate
          @update:checked="onChangeObtainType(ObtainType.pickup_friend)"
          :checked="selectedObtainType === ObtainType.pickup_friend"
          :value="ObtainType.pickup_friend"
          name="orderHowToObtain"
          :data-test-id="`${TEST_ID}-${ObtainType.pickup_friend}`"
        >
          {{ t('variant.pickup_friend.label') }}
        </BaseRadioPlate>
        <BaseCollapse
          :visible="selectedObtainType === ObtainType.pickup_friend"
          content-class="orderHowToObtain__pickup"
        >
          {{ t('variant.pickup_friend.description') }}
        </BaseCollapse>
      </div>
    </BaseCollapse>
  </div>
</template>

<i18n src="~/locales/company.yaml" />
<i18n>
ru:
  title: Способ получения
  variant:
    email:
      label: Доставка на e-mail
    courier:
      label: Курьерская доставка
      address: Добавить адрес
    points:
      label: Пункты выдачи заказов и постаматы
    pickup:
      label: Самовывоз
      description: |
        Самовывоз из магазина по адресу:
        {address}
        (часы работы: {openingHours})

        {additional}
      additional: Мы пришлем вам СМС, как только заказ прибудет в магазин.
    pickup_friend:
      label: Самовывоз с Челюскинская, 13
      description: |
        Самовывоз с Челюскинская, 13
      additional: Мы пришлем вам СМС, как только заказ прибудет в магазин.
  message:
    error: Во время выбора способа получения произошла ошибка
    action: Вернуться

en:
  title: Method of obtaining
  variant:
    email:
      label: Delivery by e-mail
    courier:
      label: Courier delivery
      address: Add address
    points:
      label: Order pickup points and parcel lockers
    pickup:
      label: Self-pickup
      description: |
        Self-pickup from the store at:
        {address}
        (working hours: {openingHours})

        {additional}
      additional: We will send you an SMS as soon as the order arrives at the store.
    pickup_friend:
      label: Self-pickup Cheluskinskaya 13
      description: |
        Self-pickup from the pickup_friend
      additional: We will send you an SMS as soon as the order arrives at the store.
  message:
    error: An error occurred while choosing the method of obtaining
    action: Return
</i18n>

<style lang="scss">
.orderHowToObtain {
  $self: &;

  background: #ffffff;
  border-radius: 8px;
  padding: 0 20px;

  &_bordered {
    border: 1px solid #121212;

    #{$self}__content {
      border-top-color: #121212;
    }
  }

  &__header {
    height: 64px;
    display: flex;
    align-items: center;
    font-size: 18px;
    line-height: 22px;
    letter-spacing: 0.2px;
    color: #c0c0c0;
    transition: color 0.3s ease;

    &_enabled {
      color: #121212;
    }
  }

  &__content {
    border-top: 1px solid rgba(18, 18, 18, 0.2);
    padding: 16px 0 26px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__courierGroup {
    padding-top: 16px;
    padding-left: 40px;
    max-width: 476px;
    display: flex;
    flex-direction: column;

    @include mq('sm') {
      padding-left: 0;
    }
  }

  &__courierAddAddress {
    height: 16px;
    display: flex;
    align-items: center;
    font-size: 9px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #c0c0c0;
    cursor: pointer;
    background: none;
    padding: 0;
    border: none;
    margin-top: 20px;
    margin-bottom: 16px;

    & &_icon {
      width: 9px;
      margin-left: 5px;
    }
  }

  &__courierAddressForm {
    padding-top: 16px;
    padding-bottom: 8px;
  }

  &__pointsGroup {
    padding-top: 24px;
    padding-bottom: 8px;
  }

  &__pickup {
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.2px;
    color: #121212;
    padding-top: 24px;
    white-space: pre-wrap;

    &_additional {
      color: #c0c0c0;
    }
  }
}
</style>
